// src/contexts/JobsContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface JobsContextType {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  locationFilter: string;
  setLocationFilter: (f: string) => void;
  typeFilter: string;
  setTypeFilter: (f: string) => void;
  remoteFilter: string;
  setRemoteFilter: (f: string) => void;
  scrollY: number;
  setScrollY: (y: number) => void;
  seenJobs: Set<string>;
  markJobSeen: (id: string) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  jobsPerPage: number;
}

const JobsContext = createContext<JobsContextType | undefined>(undefined);

export const JobsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [remoteFilter, setRemoteFilter] = useState("");
  const [scrollY, setScrollY] = useState(0);
  const [seenJobs, setSeenJobs] = useState<Set<string>>(new Set());

  const location = useLocation();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 30; // or 20, adjust as you like

  // reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, locationFilter, typeFilter, remoteFilter]);

  // ✅ Load "seen" jobs from localStorage on startup
  useEffect(() => {
    const stored = localStorage.getItem("seenJobs");
    if (stored) setSeenJobs(new Set(JSON.parse(stored)));
  }, []);

  // ✅ Save "seen" jobs to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("seenJobs", JSON.stringify([...seenJobs]));
  }, [seenJobs]);

  // ✅ Mark job as seen
  const markJobSeen = (id: string) => {
    setSeenJobs(prev => new Set(prev).add(id));
  };

  // ✅ Initialize filters from URL
  useEffect(() => {
    if (!location.pathname.startsWith("/jobs")) return;
    const params = new URLSearchParams(location.search);
    setSearchQuery(params.get("search") || "");
    setLocationFilter(params.get("location") || "");
    setTypeFilter(params.get("type") || "");
    setRemoteFilter(params.get("remote") || "");
  }, [location.pathname]);

  // ✅ Keep URL in sync with filters
  useEffect(() => {
    if (!location.pathname.startsWith("/jobs")) return;
    const params = new URLSearchParams();
    if (searchQuery) params.set("search", searchQuery);
    if (locationFilter) params.set("location", locationFilter);
    if (typeFilter) params.set("type", typeFilter);
    if (remoteFilter) params.set("remote", remoteFilter);
    navigate({ search: params.toString() }, { replace: true });
  }, [searchQuery, locationFilter, typeFilter, remoteFilter]);

  return (
    <JobsContext.Provider
      value={{
        searchQuery,
        setSearchQuery,
        locationFilter,
        setLocationFilter,
        typeFilter,
        setTypeFilter,
        remoteFilter,
        setRemoteFilter,
        scrollY,
        setScrollY,
        seenJobs,
        markJobSeen,
        currentPage,
        setCurrentPage,
        jobsPerPage,
      }}
    >
      {children}
    </JobsContext.Provider>
  );
};

export const useJobsContext = () => {
  const ctx = useContext(JobsContext);
  if (!ctx) throw new Error("useJobsContext must be used within JobsProvider");
  return ctx;
};
